/*
  skm-bla-i-boka: gjør sideraden i sections/skm-v2-bla-i-boka.liquid om til en bok man blar i.

  - Bygges først når seksjonen nærmer seg skjermen (IntersectionObserver, 600 px margin).
  - Oppslag på brede flater (≥ 720 px), én side på smale. Byttes når bredden krysser grensen.
  - Sidevendingen er et blad av N nestede striper med forside og bakside. Hver stripe roteres litt
    mer enn den forrige, så bladet bøyer seg som papir, med skygge per stripe.
  - Dra i siden (mus eller finger), klikk eller trykk, piltaster, Home/End, og knapper.
  - prefers-reduced-motion: ingen animasjon, siden byttes direkte.
  - Ingen tredjepartskode, ingen cookies, ingen lagring.
*/

const BRED = 720;
const LUFT = '600px 0px';
const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
const myk = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const PIL = (d) =>
  `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d > 0 ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

class SkmBlaIBoka extends HTMLElement {
  connectedCallback() {
    if (this.bygget) return;
    if (!('IntersectionObserver' in window)) return this.bygg();
    this.io = new IntersectionObserver(
      (inn) => {
        if (inn.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          this.bygg();
        }
      },
      { rootMargin: LUFT }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.ro?.disconnect();
  }

  bygg() {
    this.bygget = true;
    this.sider = [...this.querySelectorAll('.skm-v2-bib__side')].map((li) => {
      const img = li.querySelector('img');
      return { side: Number(li.dataset.side), src: img.getAttribute('src'), srcset: img.getAttribute('srcset') || '', alt: img.alt };
    });
    if (this.sider.length < 2) return;
    this.indeks = new Map(this.sider.map((s, i) => [s.side, i]));
    this.maksSide = Math.max(...this.sider.map((s) => s.side));
    const d = this.dataset;
    this.insertAdjacentHTML('beforeend', `
      <div class="skm-bok">
        <div class="skm-bok__vindu">
          <div class="skm-bok__scene" tabindex="0" role="region" aria-roledescription="bok">
            <span class="skm-bok__kant skm-bok__kant--v"></span><span class="skm-bok__kant skm-bok__kant--h"></span>
            <div class="skm-bok__under skm-bok__under--v"></div>
            <div class="skm-bok__under skm-bok__under--h"></div>
            <div class="skm-bok__skygge"></div>
            <div class="skm-bok__blad" aria-hidden="true"></div>
          </div>
        </div>
        <div class="skm-bok__meta">
          <button class="skm-bok__knapp skm-bok__forrige" type="button" aria-label="">${PIL(-1)}</button>
          <span class="skm-bok__status" aria-live="polite"></span>
          <button class="skm-bok__knapp skm-bok__neste" type="button" aria-label="">${PIL(1)}</button>
          <button class="skm-bok__knapp skm-bok__fullskjerm" type="button"></button>
        </div>
        <p class="skm-bok__hint"></p>
      </div>`);
    this.classList.add('er-bok');
    this.bok = this.querySelector('.skm-bok');
    this.scene = this.querySelector('.skm-bok__scene');
    this.uV = this.querySelector('.skm-bok__under--v');
    this.uH = this.querySelector('.skm-bok__under--h');
    this.blad = this.querySelector('.skm-bok__blad');
    this.skygge = this.querySelector('.skm-bok__skygge');
    this.status = this.querySelector('.skm-bok__status');
    this.forrige = this.querySelector('.skm-bok__forrige');
    this.neste = this.querySelector('.skm-bok__neste');
    this.fs = this.querySelector('.skm-bok__fullskjerm');
    this.scene.setAttribute('aria-label', d.etikett || 'Bla i boka');
    this.forrige.setAttribute('aria-label', d.forrige || '');
    this.neste.setAttribute('aria-label', d.neste || '');
    if (!document.fullscreenEnabled) this.fs.hidden = true;
    this.visning = 0;
    this.opptatt = false;
    this.lastet = new Map();

    this.forrige.addEventListener('click', () => this.ga(this.visning - 1));
    this.neste.addEventListener('click', () => this.ga(this.visning + 1));
    this.fs.addEventListener('click', () => this.fullskjerm());
    document.addEventListener('fullscreenchange', () => this.tegn());
    this.scene.addEventListener('keydown', (e) => this.tast(e));
    this.scene.addEventListener('pointerdown', (e) => this.ned(e));
    this.scene.addEventListener('pointermove', (e) => this.flytt(e));
    this.scene.addEventListener('pointerup', (e) => this.opp(e));
    this.scene.addEventListener('pointercancel', (e) => this.opp(e));

    this.modus = this.finnModus();
    if (this.tom(0)) this.visning = this.nesteIkkeTom(0, 1) ?? 0;
    this.ro = new ResizeObserver(() => this.sjekkModus());
    this.ro.observe(this);
    this.tegn();
  }

  finnModus() {
    return this.clientWidth >= BRED ? 'oppslag' : 'enkel';
  }

  sjekkModus() {
    const m = this.finnModus();
    if (m === this.modus || this.opptatt) return false;
    const side = this.forsteSynlige();
    this.modus = m;
    this.visning = this.visningFor(side);
    this.tegn();
    return true;
  }

  /* Oppslag følger bokas sidenummer i filrekka: visning v har side 2v til venstre og 2v+1 til høyre,
     og visning 0 er omslaget (side 1) alene. Mangler en fil, står plassen tom, så resten av oppslagene
     blir som i boka. På smale flater er visning = indeks i lista over sidene som finnes. */
  sisteVisning() {
    return this.modus === 'oppslag' ? Math.floor(this.maksSide / 2) : this.sider.length - 1;
  }
  visningFor(i) {
    return this.modus === 'oppslag' ? Math.floor(this.sider[i].side / 2) : i;
  }
  forsteSynlige() {
    if (this.modus === 'enkel') return this.visning;
    const i = this.venstre(this.visning) ?? this.hoyre(this.visning);
    return i ?? 0;
  }
  side(i) {
    return i >= 0 && i < this.sider.length ? i : null;
  }
  venstre(v) {
    return this.indeks.get(2 * v) ?? null;
  }
  hoyre(v) {
    return this.indeks.get(2 * v + 1) ?? null;
  }
  tom(v) {
    return this.modus === 'oppslag' && this.venstre(v) === null && this.hoyre(v) === null;
  }

  sizes() {
    const full = document.fullscreenElement === this.bok;
    if (this.modus === 'oppslag') return full ? '48vw' : '(min-width: 1240px) 490px, 46vw';
    return full ? '94vw' : 'min(460px, 92vw)';
  }

  /* Et bilde av side i. Med fast = true brukes URL-en som alt er lastet, så bladets kopier ikke henter på nytt. */
  bilde(i, fast) {
    const s = this.sider[i];
    const img = document.createElement('img');
    img.decoding = 'async';
    img.draggable = false;
    const kjent = this.lastet.get(i);
    if (fast && kjent) {
      img.src = kjent;
      img.alt = '';
    } else {
      img.srcset = s.srcset;
      img.sizes = this.sizes();
      img.src = s.src;
      img.alt = s.alt;
      img.addEventListener('load', () => this.lastet.set(i, img.currentSrc || img.src), { once: true });
    }
    return img;
  }

  settUnder(el, i) {
    el.replaceChildren();
    el.classList.toggle('har', i !== null);
    if (i !== null) el.append(this.bilde(i, false));
  }

  /* Bredden på sidebunken til venstre og høyre, etter hvor langt i boka man er. */
  kanter(v) {
    const siste = this.sisteVisning() || 1;
    const maks = this.modus === 'oppslag' ? 12 : 6;
    const frem = Math.min(1, Math.max(0, v / siste));
    this.querySelector('.skm-bok__kant--v').style.setProperty('--kant', `${Math.round(maks * frem)}px`);
    this.querySelector('.skm-bok__kant--h').style.setProperty('--kant', `${Math.round(maks * (1 - frem))}px`);
  }

  forvarm(i) {
    if (i === null || this.lastet.has(i)) return;
    const img = new Image();
    img.decoding = 'async';
    img.sizes = this.sizes();
    img.srcset = this.sider[i].srcset;
    img.src = this.sider[i].src;
    img.addEventListener('load', () => this.lastet.set(i, img.currentSrc || img.src), { once: true });
  }

  tegn() {
    if (!this.bok) return;
    this.bok.classList.toggle('skm-bok--oppslag', this.modus === 'oppslag');
    this.bok.classList.toggle('skm-bok--enkel', this.modus === 'enkel');
    const v = this.visning;
    let synlige;
    if (this.modus === 'oppslag') {
      this.settUnder(this.uV, this.venstre(v));
      this.settUnder(this.uH, this.hoyre(v));
      const v0 = this.venstre(v) === null, h0 = this.hoyre(v) === null;
      this.scene.classList.toggle('er-apen', !v0 && !h0);
      this.scene.style.transform = v0 && !h0 ? 'translateX(-25%)' : h0 && !v0 ? 'translateX(25%)' : '';
      synlige = [this.venstre(v), this.hoyre(v)].filter((i) => i !== null);
      [this.venstre(v + 1), this.hoyre(v + 1), this.venstre(v - 1), this.hoyre(v - 1)].forEach((i) => this.forvarm(i));
    } else {
      this.settUnder(this.uV, null);
      this.settUnder(this.uH, v);
      this.scene.style.transform = '';
      synlige = [v];
      [this.side(v + 1), this.side(v - 1)].forEach((i) => this.forvarm(i));
    }
    this.kanter(v);
    const nr = synlige.map((i) => this.sider[i].side);
    this.status.innerHTML = `Side <strong>${nr.join('–')}</strong> av ${this.maksSide}`;
    this.neste.disabled = this.nesteIkkeTom(v, 1) === null;
    this.forrige.disabled = this.nesteIkkeTom(v, -1) === null;
    this.querySelector('.skm-bok__hint').textContent = this.modus === 'oppslag' ? this.dataset.hintDesktop : this.dataset.hintMobil;
    this.fs.textContent = document.fullscreenElement === this.bok ? this.dataset.lukkFullskjerm : this.dataset.fullskjerm;
  }

  /* Bladet: N nestede striper. Forsiden viser forside-bildet, baksiden bakside-bildet (eller papir). */
  byggBlad(forside, bakside) {
    this.blad.replaceChildren();
    this.striper = [];
    const N = this.modus === 'oppslag' ? 24 : 16;
    const W = this.blad.clientWidth;
    const w = W / N;
    let forelder = this.blad;
    for (let k = 0; k < N; k++) {
      const st = document.createElement('div');
      st.className = 'skm-bok__stripe';
      st.style.width = `${w + 1.5}px`;
      st.style.left = k === 0 ? '0px' : `${w}px`;
      const f = document.createElement('div');
      f.className = 'skm-bok__flate skm-bok__flate--f';
      if (forside === null) f.classList.add('skm-bok__flate--papir');
      else {
        const fi = document.createElement('div');
        fi.style.cssText = `width:${W}px;left:${-k * w}px`;
        fi.append(this.bilde(forside, true));
        f.append(fi);
      }
      const b = document.createElement('div');
      b.className = 'skm-bok__flate skm-bok__flate--b';
      if (bakside === null) b.classList.add('skm-bok__flate--papir');
      else {
        const bi = document.createElement('div');
        bi.style.cssText = `width:${W}px;left:${-(W - (k + 1) * w)}px`;
        bi.append(this.bilde(bakside, true));
        b.append(bi);
      }
      st.append(f, b);
      forelder.append(st);
      this.striper.push(st);
      forelder = st;
    }
  }

  vinkel(A) {
    const N = this.striper.length;
    const r = Math.sin((A * Math.PI) / 180);
    const boy = r * (this.modus === 'oppslag' ? 46 : 38);
    const t = (k) => Math.max(0, Math.min(180, A - boy / 2 + (boy * k) / N));
    const sh = (x) => ((1 - Math.abs(Math.cos((x * Math.PI) / 180))) * 0.32).toFixed(3);
    const hl = (x) => (x < 90 ? Math.max(0, Math.cos((x * Math.PI) / 180)) * r * 0.22 : 0).toFixed(3);
    let forrige = 0;
    this.striper.forEach((st, k) => {
      const midt = t(k + 0.5);
      st.style.transform = `rotateY(${-(midt - forrige)}deg)`;
      st.style.setProperty('--sh0', sh(t(k)));
      st.style.setProperty('--sh1', sh(t(k + 1)));
      st.style.setProperty('--hl0', hl(t(k)));
      st.style.setProperty('--hl1', hl(t(k + 1)));
      forrige = midt;
    });
    const oppslag = this.modus === 'oppslag';
    this.skygge.style.opacity = (r * (A < 90 ? 0.9 : 0.5)).toFixed(3);
    this.skygge.style.left = oppslag && A < 90 ? '50%' : '0';
    this.skygge.style.transform = oppslag && A >= 90 ? 'scaleX(-1)' : '';
  }

  start(retning, mal) {
    if (this.opptatt || mal < 0 || mal > this.sisteVisning() || mal === this.visning) return false;
    this.opptatt = true;
    const v = this.visning;
    if (this.modus === 'oppslag') {
      if (retning > 0) {
        this.settUnder(this.uV, this.venstre(v));
        this.settUnder(this.uH, this.hoyre(mal));
        this.byggBlad(this.hoyre(v), this.venstre(mal));
      } else {
        this.settUnder(this.uV, this.venstre(mal));
        this.settUnder(this.uH, this.hoyre(v));
        this.byggBlad(this.hoyre(mal), this.venstre(v));
      }
      this.scene.style.transform = '';
      this.scene.classList.add('er-apen');
    } else {
      this.settUnder(this.uH, retning > 0 ? mal : v);
      this.byggBlad(retning > 0 ? v : mal, null);
    }
    this.vending = { retning, mal, A: retning > 0 ? 0 : 180 };
    this.vinkel(this.vending.A);
    return true;
  }

  fullfor(tilEnden) {
    const vd = this.vending;
    const ende = tilEnden === (vd.retning > 0) ? 180 : 0;
    const fra = vd.A;
    const varighet = RM.matches ? 0 : 260 + Math.abs(ende - fra) * 3.2;
    const t0 = performance.now();
    const steg = (na) => {
      const p = varighet ? Math.min(1, (na - t0) / varighet) : 1;
      vd.A = fra + (ende - fra) * myk(p);
      this.vinkel(vd.A);
      if (p < 1) return requestAnimationFrame(steg);
      if (tilEnden) this.visning = vd.mal;
      this.blad.replaceChildren();
      this.skygge.style.opacity = '0';
      this.opptatt = false;
      this.vending = null;
      if (!this.sjekkModus()) this.tegn();
    };
    requestAnimationFrame(steg);
  }

  nesteIkkeTom(v, r) {
    for (let m = v + r; m >= 0 && m <= this.sisteVisning(); m += r) if (!this.tom(m)) return m;
    return null;
  }

  ga(mal) {
    const r = Math.sign(mal - this.visning);
    if (!r) return;
    mal = Math.max(0, Math.min(this.sisteVisning(), mal));
    if (this.tom(mal)) mal = this.nesteIkkeTom(mal, r) ?? this.nesteIkkeTom(mal, -r);
    if (mal !== null && mal !== this.visning && this.start(Math.sign(mal - this.visning), mal)) this.fullfor(true);
  }

  tast(e) {
    const til = { ArrowRight: this.visning + 1, PageDown: this.visning + 1, ArrowLeft: this.visning - 1, PageUp: this.visning - 1, Home: 0, End: this.sisteVisning() }[e.key];
    if (til === undefined) return;
    e.preventDefault();
    this.ga(til);
  }

  ned(e) {
    if (this.opptatt || e.button) return;
    this.dra = { x0: e.clientX, y0: e.clientY, r: this.scene.getBoundingClientRect(), id: e.pointerId, i_gang: false };
  }

  flytt(e) {
    const d = this.dra;
    if (!d || e.pointerId !== d.id) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.i_gang) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      const retning = this.modus === 'oppslag' ? (d.x0 > d.r.left + d.r.width / 2 ? 1 : -1) : dx < 0 ? 1 : -1;
      const mal = this.nesteIkkeTom(this.visning, retning);
      if (mal === null || !this.start(retning, mal)) {
        this.dra = null;
        return;
      }
      d.i_gang = true;
      try {
        this.scene.setPointerCapture(e.pointerId);
      } catch (_) {}
      this.scene.classList.add('drar');
    }
    let A;
    if (this.modus === 'oppslag') {
      const rygg = d.r.left + d.r.width / 2;
      A = (Math.acos(Math.max(-1, Math.min(1, (e.clientX - rygg) / (d.r.width / 2)))) * 180) / Math.PI;
    } else {
      const q = Math.max(0, Math.min(1, Math.abs(dx) / d.r.width));
      A = (Math.acos(this.vending.retning > 0 ? 1 - q : q) * 180) / Math.PI;
    }
    this.vending.A = A;
    this.vinkel(A);
  }

  opp(e) {
    const d = this.dra;
    if (!d || e.pointerId !== d.id) return;
    this.dra = null;
    this.scene.classList.remove('drar');
    if (d.i_gang) {
      const A = this.vending.A;
      const fram = this.vending.retning > 0;
      const terskel = this.modus === 'oppslag' ? (fram ? A > 70 : A < 110) : fram ? A > 40 : A < 75;
      this.fullfor(terskel);
      return;
    }
    if (e.type !== 'pointerup') return;
    const x = (d.x0 - d.r.left) / d.r.width;
    if (this.modus === 'oppslag') this.ga(this.visning + (x > 0.5 ? 1 : -1));
    else this.ga(this.visning + (x < 0.3 ? -1 : 1));
  }

  fullskjerm() {
    if (document.fullscreenElement) document.exitFullscreen();
    else this.bok.requestFullscreen?.();
  }
}

if (!customElements.get('skm-bla-i-boka')) customElements.define('skm-bla-i-boka', SkmBlaIBoka);
